import React, { Fragment, useEffect } from 'react';
import { Link } from '@reach/router';
import {
  selectServicesKapp,
  CardCol,
  CardRow,
  TimeAgo,
  Utils,
  C,
} from '@kineticdata/bundle-common';
import { I18n } from '@kineticdata/react';
import { Collapse } from 'reactstrap';
import classNames from 'classnames';

import { DefaultNode } from './activity_node_types/Default';
import { ChangeNode } from './activity_node_types/Change';
import { IncidentNode } from './activity_node_types/Incident';
import { TaskNode } from './activity_node_types/Task';
import { BlockTextNode } from './activity_node_types/BlockText';
import { MediaSanitationNode } from './activity_node_types/MediaSanitation';

// Helper to get the correct component given the activity type.
const getActivityNode = activityType => {
  switch (activityType) {
    case 'ITSM Change':
      return ChangeNode;
    case 'ITSM Incident':
      return IncidentNode;
    case 'ITSM Task':
      return TaskNode;
    case 'Block Text':
    case 'Block Text Medium':
    case 'Block Text Small':
    case 'Block Text Xtra Small':
    case 'Action Block Text':
    case 'Action Block Text Medium':
    case 'Action Block Text Small':
    case 'Action Block Text Xtra Small':
    case 'Approval Block Text':
    case 'Approval Block Text Medium':
    case 'Approval Block Text Small':
    case 'Approval Block Text Xtra Small':
      return BlockTextNode;
    case 'Media Sanitation Block Text':
    case 'Media Sanitation Text Medium':
    case 'Media Sanitation Text Small':
    case 'Media Sanitation Text Xtra Small':
      return MediaSanitationNode;
    default:
      return DefaultNode;
  }
};

// Helper to get the correct body component given the activity type.
const getActivityNodeClass = activityType => {
  switch (activityType) {
    case 'Block Text':
    case 'Action Block Text':
    case 'Approval Block Text':
    case 'Media Sanitation Block Text':
      return 'block-text';
    case 'Block Text Medium':
    case 'Action Block Text Medium':
    case 'Approval Block Text Medium':
    case 'Media Sanitation Text Medium':
      return 'block-text block-text--md';
    case 'Block Text Small':
    case 'Action Block Text Small':
    case 'Approval Block Text Small':
    case 'Media Sanitation Text Small':
      return 'block-text block-text--sm';
    case 'Block Text Xtra Small':
    case 'Action Block Text Xtra Small':
    case 'Approval Block Text Xtra Small':
    case 'Media Sanitation Text Xtra Small':
      return 'block-text block-text--xs';
    default:
      return undefined;
  }
};

export const ActivityNode = props => {
  const Node = getActivityNode(props.activity.type);
  return (
    <Node {...props} className={getActivityNodeClass(props.activity.type)} />
  );
};

/***** Helper functions and components *****/

const getDataList = (data, exclude) =>
  Object.keys(data)
    // Map to a list of objects with label, value, and key properties
    .map(key => ({ key, label: key, value: data[key] }))
    // Filter out keys with falsey values, and any keys matching the exclude array
    .filter(
      ({ label, value }) =>
        value && (!Array.isArray(exclude) || !exclude.includes(label)),
    );

export const standardConnectMapper = ({ state, props, dataListExcludes }) => {
  // List of data properties to display for the activity record
  const dataList = getDataList(props.data, dataListExcludes);
  // Link URL if one exists
  const link =
    (Utils.hasAttributeValue(
      state.app.profile,
      C.ATTRIBUTE_USER_COMPANY,
      Utils.getAttributeValues(
        state.app.space,
        C.SPACE_ATTR_ITSM_WEB_URL_COMPANIES,
      ),
    ) &&
      props.data['InternalLink']) ||
    props.data['Link'];
  // Flag defining if the link is a download
  const isDownload =
    `${props.data['DownloadInternalLink']}`.toLowerCase() === 'true';
  // The approval related to this activity if one exists
  const approval =
    props.activity.type === 'Approval' && props.data['Reference Id']
      ? props.submission.descendants.find(
          d =>
            d.type === 'Approval' &&
            d.coreState === 'Draft' &&
            d.id === props.data['Reference Id'] &&
            !!d.currentPage,
        )
      : null;
  // The action related to this activity if one exists
  const action =
    props.activity.type === 'Action' && props.data['Reference Id']
      ? props.submission.descendants.find(
          d =>
            d.type === 'Action' &&
            d.coreState === 'Draft' &&
            d.id === props.data['Reference Id'] &&
            !!d.currentPage,
        )
      : null;

  return {
    appLocation: state.app.location,
    location: state.router.location,
    servicesKapp: selectServicesKapp(state),
    dataList,
    hasDataList: dataList.length > 0,
    link,
    isDownload,
    approval,
    action,
    isMobile: ['small'].includes(state.app.layoutSize),
  };
};

const getStatusBadgeColor = (status = '') => {
  switch (status) {
    case 'Complete':
    case 'Completed':
    case 'Approved':
    case 'No Approval Needed':
    case 'Informational':
    case 'Closed':
    case 'Resolved':
    case 'Auto-Approved':
      return 'badge-success';
    default:
      return 'badge-info';
  }
};

export const DefaultDataContent = ({ label, value, className }) => (
  <div className={className}>
    {label &&
      label !== 'STRING' && (
        <dt>
          <span className={'title'}>
            <I18n>{label}</I18n>
          </span>
        </dt>
      )}
    {value && (
      <dd>
        <I18n>{value}</I18n>
      </dd>
    )}
  </div>
);

export const DefaultToggleButton = ({ isOpen, toggle }) => {
  return (
    <button className="btn" onClick={toggle} disabled={!toggle}>
      <span
        className={classNames('fa fa-lg bg-light', {
          'fa-plus-circle': !!toggle && !isOpen,
          'fa-minus-circle': !!toggle && isOpen,
          'fa-dot-circle': !toggle,
        })}
      />
    </button>
  );
};

export const DefaultHeaderComponent = ({
  status,
  statusColor,
  title,
  link,
  isDownload,
  meta,
  isOpen,
  toggle,
  children,
  isMobile,
}) => {
  const statusRow = status && (
    <CardRow className="card__row--status">
      <span
        className={classNames(
          'badge badge-stylized text-muted',
          statusColor || getStatusBadgeColor(status),
        )}
      >
        <I18n>{status}</I18n>
      </span>
    </CardRow>
  );
  const titleRow = (
    <CardRow type="subtitle">
      <span>
        <I18n>{title}</I18n>
      </span>
      {link && (
        <span className="ml-auto pl-3">
          <a
            href={link}
            download={isDownload}
            target="_blank"
            rel="noopener noreferrer"
            title={isDownload ? 'Download File' : 'Open Link'}
          >
            <span
              className={classNames('fa fa-lg', {
                'fa-download': isDownload,
                'fa-external-link-alt': !isDownload,
              })}
            />
          </a>
        </span>
      )}
    </CardRow>
  );
  const metaRow = (
    <CardRow type="meta" className="text-muted">
      {Array.isArray(meta) &&
        meta.filter(m => !!m.value).map((m, i) => (
          <Fragment key={`activity-meta-${i}`}>
            {i !== 0 && <span>{' \u00b7 '}</span>}
            {m.label && (
              <span>
                <I18n>{m.label}</I18n>{' '}
              </span>
            )}
            <strong>
              {m.timeAgo ? <TimeAgo timestamp={m.value} /> : m.value}
            </strong>
          </Fragment>
        ))}
    </CardRow>
  );
  const toggleMoreRow = typeof toggle === 'function' && (
    <CardRow className="card__row--toggle">
      <button className="btn btn-text" onClick={toggle}>
        <span
          className={classNames('fa fa-fw fa-md text-muted', {
            'fa-plus': !isOpen,
            'fa-minus': isOpen,
          })}
        />
        <span>
          <I18n>{isOpen ? 'Show Less' : 'Show More'}</I18n>
        </span>
      </button>
    </CardRow>
  );
  return typeof children === 'function' ? (
    children({ statusRow, titleRow, metaRow, toggleMoreRow })
  ) : (
    <CardRow>
      {isMobile ? (
        <>
          <CardCol>
            {statusRow}
            {titleRow}
            {metaRow}
            {toggleMoreRow}
          </CardCol>
        </>
      ) : (
        <>
          <CardCol>
            {titleRow}
            {metaRow}
          </CardCol>
          <CardCol className="card__col--append-meta">
            {statusRow}
            {toggleMoreRow}
          </CardCol>
        </>
      )}
    </CardRow>
  );
};

export const DefaultBodyComponent = ({
  dataList,
  approval,
  action,
  appLocation,
  location,
  isOpen,
  toggle,
  children,
}) => {
  useEffect(() => {
    // If the node has an action or approval, auto expand it on mount
    if ((!!action || !!approval) && !isOpen && typeof toggle === 'function') {
      toggle();
    }
  }, []);
  
  const returnToRequestType =
    (location &&
      (location.pathname.match(/requests\/(.*?)\/request\//i) || [])[1]) ||
    '';
  const dataRow = dataList.length > 0 && (
    <CardRow>
      <dl>{dataList.map(props => <DefaultDataContent {...props} />)}</dl>
    </CardRow>
  );
  const approvalRow = approval && (
    <CardRow>
      <Link
        className="btn btn-tertiary"
        to={`${appLocation}/approvals/approval/${
          approval.id
        }?returnToRequest=${returnToRequestType}`}
      >
        <I18n>View Approval and Approve/Deny</I18n>
      </Link>
    </CardRow>
  );
  const actionRow = action && (
    <CardRow>
      <Link
        className="btn btn-tertiary"
        to={`${appLocation}/actions/action/${
          action.id
        }?returnToRequest=${returnToRequestType}`}
      >
        <I18n>View Action and Complete/Cancel</I18n>
      </Link>
    </CardRow>
  );
  const toggleMoreRow = typeof toggle === 'function' && (
    <CardRow className="card__row--toggle">
      <button className="btn btn-text" onClick={toggle}>
        <span
          className={classNames('fa fa-fw fa-md text-muted', {
            'fa-plus': !isOpen,
            'fa-minus': isOpen,
          })}
        />
        <span>
          <I18n>{isOpen ? 'Show Less' : 'Show More'}</I18n>
        </span>
      </button>
    </CardRow>
  );
  return (
    <Collapse tag={CardCol} isOpen={isOpen} toggle={toggle}>
      {typeof children === 'function' ? (
        children({ dataRow, approvalRow, actionRow, toggleMoreRow })
      ) : (
        <>
          {dataRow}
          {approvalRow}
          {actionRow}
          {children}
          {toggleMoreRow}
        </>
      )}
    </Collapse>
  );
};
